import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../api/client';
import { FireLoader } from '../components/Loader';
import {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirm,
} from '../utils/alert';

export default function AddClass() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Teachers – single selection
  const [teachers, setTeachers] = useState([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState('');
  const [showTeacherSelect, setShowTeacherSelect] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);

  // Subjects – multiple selection
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('');
  const [showSubjectSelect, setShowSubjectSelect] = useState(false);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    level: 'primary',
    faculty: 'general',
    description: '',
    status: 'active',
  });

  const levels = [
    { value: 'nursery', label: 'Nursery' },
    { value: 'primary', label: 'Primary' },
    { value: 'jss', label: 'JSS' },
    { value: 'sss', label: 'SSS' },
  ];

  const faculties = [
    { value: 'general', label: 'General' },
    { value: 'science', label: 'Science' },
    { value: 'arts', label: 'Arts' },
    { value: 'commerce', label: 'Commerce' },
  ];

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'archived', label: 'Archived' },
  ];

  // Fetch teachers and subjects on mount
  useEffect(() => {
    fetchTeachers();
    fetchSubjects();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoadingTeachers(true);
      const response = await apiClient.get('/teachers', {
        params: {
          status: 'active',
          limit: 100,
        }
      });

      let data = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else if (response.data.rows) {
          data = response.data.rows;
        }
      }
      setTeachers(data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      await showError({ text: 'Failed to load teachers list. Please refresh the page.' });
    } finally {
      setLoadingTeachers(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      const response = await apiClient.get('/subjects', {
        params: {
          is_active: true,
          limit: 100,
        }
      });

      let data = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          data = response.data;
        } else if (response.data.data) {
          data = response.data.data;
        } else if (response.data.rows) {
          data = response.data.rows;
        }
      }
      setSubjects(data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      await showError({ text: 'Failed to load subjects list. Please refresh the page.' });
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Teacher handlers – single selection
  const handleTeacherSelect = (teacherId) => {
    setSelectedTeacherId(prev => (prev === teacherId ? null : teacherId));
  };

  const handleRemoveTeacher = () => {
    setSelectedTeacherId(null);
  };

  // Subject handlers – multiple selection
  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjectIds(prev => {
      if (prev.includes(subjectId)) {
        return prev.filter(id => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const handleRemoveSubject = (subjectId) => {
    setSelectedSubjectIds(prev => prev.filter(id => id !== subjectId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      await showWarning({ title: 'Missing Name', text: 'Class name is required.' });
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        teacher_ids: selectedTeacherId ? [selectedTeacherId] : [],
        subject_ids: selectedSubjectIds.length > 0 ? selectedSubjectIds : [],
      };

      console.log('Submitting class data:', submitData);

      await apiClient.post('/classes', submitData);

      await showSuccess({ text: 'Class created successfully!', toast: true });
      navigate('/app/classes');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create class';
      await showError({ text: errorMessage });
      console.error('Error creating class:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for display
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getFullName = (teacher) => {
    return `${teacher.first_name || ''} ${teacher.middle_name || ''} ${teacher.last_name || ''}`.trim();
  };

  // Filter teachers
  const filteredTeachers = teachers.filter(teacher => {
    const fullName = getFullName(teacher).toLowerCase();
    const search = teacherSearchTerm.toLowerCase();
    return fullName.includes(search) ||
           (teacher.employee_number && teacher.employee_number.toLowerCase().includes(search)) ||
           (teacher.qualification && teacher.qualification.toLowerCase().includes(search));
  });

  // Filter subjects
  const filteredSubjects = subjects.filter(subject => {
    const search = subjectSearchTerm.toLowerCase();
    return subject.name.toLowerCase().includes(search) ||
           (subject.code && subject.code.toLowerCase().includes(search));
  });

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="CREATING..." />;
  }

  const selectedTeacher = selectedTeacherId ? teachers.find(t => t.id === selectedTeacherId) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8">
      {/* Simple Header - No Blue Card */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/app/classes')}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-600 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Add New Class</h1>
          <p className="text-sm text-gray-500 mt-0.5">Create a new class with teacher and subjects</p>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 lg:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Class Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Class Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., PRIMARY 1, JSS 2, SSS 3 Science"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 placeholder-gray-400 text-sm transition-colors"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Level <span className="text-red-500">*</span>
              </label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 text-sm transition-colors"
              >
                {levels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                The class number will be automatically assigned
              </p>
            </div>

            {/* Faculty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Faculty / Department
              </label>
              <select
                name="faculty"
                value={formData.faculty}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 text-sm transition-colors"
              >
                {faculties.map(faculty => (
                  <option key={faculty.value} value={faculty.value}>
                    {faculty.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 text-sm transition-colors"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ---------- Teacher Assignment (SINGLE) ---------- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Class Teacher <span className="text-red-500">*</span>
              <span className="text-gray-400 text-xs ml-2">(Only one teacher can be class teacher)</span>
            </label>

            {!selectedTeacher ? (
              <button
                type="button"
                onClick={() => setShowTeacherSelect(!showTeacherSelect)}
                className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-400 hover:bg-gray-50/50 transition-all text-center group"
              >
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-50 transition-colors">
                  <PlusIcon className="w-6 h-6 text-gray-400 group-hover:text-primary-500" />
                </div>
                <p className="text-sm text-gray-500 group-hover:text-primary-600 transition-colors">Click to assign a class teacher</p>
                <p className="text-xs text-gray-400">Search and select one teacher</p>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-700 font-bold text-xs">
                        {getInitials(selectedTeacher.first_name, selectedTeacher.last_name)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-700">{getFullName(selectedTeacher)}</span>
                    <button
                      type="button"
                      onClick={handleRemoveTeacher}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTeacherSelect(!showTeacherSelect)}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  >
                    Change Teacher
                  </button>
                </div>
              </div>
            )}

            {/* Teacher Selection Dropdown */}
            {showTeacherSelect && (
              <div className="mt-3 border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search teachers by name, employee number, or qualification..."
                      value={teacherSearchTerm}
                      onChange={(e) => setTeacherSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 placeholder-gray-400 text-sm transition-colors"
                    />
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {loadingTeachers ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-gray-400 mt-2">Loading teachers...</p>
                    </div>
                  ) : filteredTeachers.length > 0 ? (
                    filteredTeachers.map((teacher) => {
                      const isSelected = selectedTeacherId === teacher.id;
                      return (
                        <button
                          key={teacher.id}
                          type="button"
                          onClick={() => handleTeacherSelect(teacher.id)}
                          className={`w-full p-3 hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-50 last:border-0 ${
                            isSelected ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center flex-shrink-0">
                            {teacher.user?.profile_picture ? (
                              <img
                                src={teacher.user.profile_picture}
                                alt={getFullName(teacher)}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-primary-700 font-bold text-sm">
                                {getInitials(teacher.first_name, teacher.last_name)}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-800">{getFullName(teacher)}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span>#{teacher.employee_number}</span>
                              {teacher.qualification && (
                                <>
                                  <span>•</span>
                                  <span>{teacher.qualification}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {isSelected ? (
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      <UserIcon className="w-10 h-10 mx-auto text-gray-200 mb-2" />
                      <p>No teachers found</p>
                      <p className="text-sm">Try adjusting your search</p>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-gray-100 bg-gray-50/30 flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {selectedTeacherId ? '1 teacher selected' : 'No teacher selected'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowTeacherSelect(false);
                      setTeacherSearchTerm('');
                    }}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <UserIcon className="w-4 h-4" />
              The selected teacher will be set as the class teacher for this class.
            </p>
          </div>

          {/* ---------- Subject Assignment (MULTIPLE) ---------- */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Class Subjects
              <span className="text-gray-400 text-xs ml-2">(Select multiple)</span>
            </label>

            {selectedSubjectIds.length === 0 ? (
              <button
                type="button"
                onClick={() => setShowSubjectSelect(!showSubjectSelect)}
                className="w-full p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-primary-400 hover:bg-gray-50/50 transition-all text-center group"
              >
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary-50 transition-colors">
                  <BookOpenIcon className="w-6 h-6 text-gray-400 group-hover:text-primary-500" />
                </div>
                <p className="text-sm text-gray-500 group-hover:text-primary-600 transition-colors">Click to assign subjects</p>
                <p className="text-xs text-gray-400">Search and select from available subjects</p>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {selectedSubjectIds.map((id) => {
                    const subject = subjects.find(s => s.id === id);
                    if (!subject) return null;
                    return (
                      <div key={id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                        <BookOpenIcon className="w-4 h-4 text-primary-500" />
                        <span className="text-sm text-gray-700">{subject.name}</span>
                        {subject.code && (
                          <span className="text-xs text-gray-400 font-mono">({subject.code})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveSubject(id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setShowSubjectSelect(!showSubjectSelect)}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
                  >
                    + Add More
                  </button>
                </div>
              </div>
            )}

            {/* Subject Selection Dropdown */}
            {showSubjectSelect && (
              <div className="mt-3 border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search subjects by name or code..."
                      value={subjectSearchTerm}
                      onChange={(e) => setSubjectSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 placeholder-gray-400 text-sm transition-colors"
                    />
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {loadingSubjects ? (
                    <div className="p-8 text-center">
                      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                      <p className="text-gray-400 mt-2">Loading subjects...</p>
                    </div>
                  ) : filteredSubjects.length > 0 ? (
                    filteredSubjects.map((subject) => {
                      const isSelected = selectedSubjectIds.includes(subject.id);
                      return (
                        <button
                          key={subject.id}
                          type="button"
                          onClick={() => handleSubjectToggle(subject.id)}
                          className={`w-full p-3 hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-50 last:border-0 ${
                            isSelected ? 'bg-gray-50' : ''
                          }`}
                        >
                          <BookOpenIcon className={`w-5 h-5 ${isSelected ? 'text-primary-500' : 'text-gray-300'}`} />
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-800">{subject.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              {subject.code && (
                                <span className="font-mono">{subject.code}</span>
                              )}
                              {subject.level && (
                                <>
                                  <span>•</span>
                                  <span className="capitalize">{subject.level}</span>
                                </>
                              )}
                              {subject.subject_type && (
                                <>
                                  <span>•</span>
                                  <span className="capitalize">{subject.subject_type}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {isSelected ? (
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      <BookOpenIcon className="w-10 h-10 mx-auto text-gray-200 mb-2" />
                      <p>No subjects found</p>
                      <p className="text-sm">Try adjusting your search</p>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-gray-100 bg-gray-50/30 flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {selectedSubjectIds.length} subject(s) selected
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSubjectSelect(false);
                      setSubjectSearchTerm('');
                    }}
                    className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <BookOpenIcon className="w-4 h-4" />
              Selected subjects will be automatically assigned to this class upon creation.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Add any additional information about this class..."
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50/50 text-gray-700 placeholder-gray-400 text-sm transition-colors resize-none"
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate('/app/classes')}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium shadow-sm hover:shadow-md"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  Create Class
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}